'use client';

import { trackPublicAnalyticsEvent, type PublicAnalyticsEvent } from '@torlin/api-client';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { analyticsIdentifiers, readAnalyticsConsent, subscribeAnalyticsConsent } from '@/lib/analytics-consent';

const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? '';
const scrollMilestones = [25, 50, 75, 90, 100] as const;

function eventId(): string {
  return globalThis.crypto.randomUUID();
}

function deviceType(): PublicAnalyticsEvent['device_type'] {
  if (window.innerWidth < 768) return 'mobile';
  if (window.innerWidth < 1100) return 'tablet';
  return 'desktop';
}

function referrerHost(): string | null {
  if (!document.referrer) return null;
  try { return new URL(document.referrer).hostname; } catch { return null; }
}

function isExcludedPath(pathname: string): boolean {
  return /^\/(?:en\/|de\/)?(?:app|beach)(?:\/|$)/.test(pathname);
}

function fullAnalyticsAllowed(consent: ReturnType<typeof readAnalyticsConsent>): boolean {
  return consent === 'granted' && navigator.doNotTrack !== '1';
}

function fullContext(): Pick<PublicAnalyticsEvent, 'consent_level' | 'visitor_uuid' | 'session_uuid' | 'referrer_host' | 'locale' | 'device_type' | 'viewport_width' | 'utm_source' | 'utm_medium' | 'utm_campaign'> {
  const identifiers = analyticsIdentifiers();
  const query = new URLSearchParams(window.location.search);
  return {
    consent_level: 'full',
    visitor_uuid: identifiers.visitorUuid,
    session_uuid: identifiers.sessionUuid,
    referrer_host: referrerHost(),
    locale: document.documentElement.lang || null,
    device_type: deviceType(),
    viewport_width: window.innerWidth,
    utm_source: query.get('utm_source'),
    utm_medium: query.get('utm_medium'),
    utm_campaign: query.get('utm_campaign'),
  };
}

function deliver(event: PublicAnalyticsEvent): void {
  void trackPublicAnalyticsEvent(apiUrl, event).catch(() => undefined);
}

function sendFullEvent(
  eventName: PublicAnalyticsEvent['event_name'],
  path: string,
  fields: Partial<Pick<PublicAnalyticsEvent, 'section_name' | 'duration_ms' | 'scroll_depth' | 'metadata'>> = {},
): void {
  deliver({
    event_uuid: eventId(),
    event_name: eventName,
    path,
    ...fullContext(),
    ...fields,
  });
}

export function PublicAnalyticsTracker() {
  const pathname = usePathname();
  const consent = useSyncExternalStore<ReturnType<typeof readAnalyticsConsent>>(subscribeAnalyticsConsent, readAnalyticsConsent, () => 'unknown');
  const pageView = useRef<{ path: string; uuid: string } | null>(null);

  useEffect(() => {
    if (isExcludedPath(pathname)) return;
    if (pageView.current?.path !== pathname) pageView.current = { path: pathname, uuid: eventId() };
    const currentPageView = pageView.current;
    const full = fullAnalyticsAllowed(consent);
    const timer = window.setTimeout(() => {
      deliver({
        event_uuid: currentPageView.uuid,
        event_name: 'page_view',
        consent_level: full ? 'full' : 'minimal',
        path: pathname,
        ...(full ? fullContext() : {}),
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [consent, pathname]);

  useEffect(() => {
    if (isExcludedPath(pathname) || !fullAnalyticsAllowed(consent)) return;
    const key = 'torlin_analytics_consent_recorded';
    if (sessionStorage.getItem(key) !== '1') {
      sessionStorage.setItem(key, '1');
      sendFullEvent('consent_granted', pathname);
    }
  }, [consent, pathname]);

  useEffect(() => {
    if (isExcludedPath(pathname) || !fullAnalyticsAllowed(consent)) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-analytics-event]') : null;
      if (!target) return;
      const name = target.dataset.analyticsEvent as PublicAnalyticsEvent['event_name'] | undefined;
      if (!name || name === 'page_view') return;
      sendFullEvent(name, pathname, { metadata: { target: target.dataset.analyticsTarget ?? 'unknown' } });
    };
    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, [consent, pathname]);

  useEffect(() => {
    if (isExcludedPath(pathname) || !fullAnalyticsAllowed(consent)) return;
    const seen = new Set<string>();
    const timers = new Map<Element, number>();
    const sections = Array.from(document.querySelectorAll<HTMLElement>('main section[id], main [data-analytics-section]'));
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const element = entry.target as HTMLElement;
        const name = element.dataset.analyticsSection || element.id;
        if (!name || seen.has(name)) continue;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
          if (!timers.has(element)) {
            timers.set(element, window.setTimeout(() => {
              seen.add(name);
              timers.delete(element);
              sendFullEvent('section_view', pathname, { section_name: name });
            }, 2000));
          }
        } else {
          const timer = timers.get(element);
          if (timer !== undefined) window.clearTimeout(timer);
          timers.delete(element);
        }
      }
    }, { threshold: [0, 0.2, 0.5] });
    sections.forEach((section) => observer.observe(section));
    return () => {
      observer.disconnect();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [consent, pathname]);

  useEffect(() => {
    if (isExcludedPath(pathname) || !fullAnalyticsAllowed(consent)) return;
    const sent = new Set<number>();
    const onScroll = () => {
      const scrollable = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const depth = Math.min(100, Math.round(window.scrollY / scrollable * 100));
      for (const milestone of scrollMilestones) {
        if (depth >= milestone && !sent.has(milestone)) {
          sent.add(milestone);
          sendFullEvent('scroll_depth', pathname, { scroll_depth: milestone });
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [consent, pathname]);

  useEffect(() => {
    if (isExcludedPath(pathname) || !fullAnalyticsAllowed(consent)) return;
    let visibleSince = document.visibilityState === 'visible' ? performance.now() : null;
    let accumulated = 0;

    const flush = () => {
      if (visibleSince !== null) {
        accumulated += performance.now() - visibleSince;
        visibleSince = performance.now();
      }
      const duration = Math.round(accumulated);
      if (duration >= 1000) sendFullEvent('engagement', pathname, { duration_ms: duration });
      accumulated = 0;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flush();
        visibleSince = null;
      } else if (visibleSince === null) {
        visibleSince = performance.now();
      }
    };
    const interval = window.setInterval(flush, 15000);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      flush();
    };
  }, [consent, pathname]);

  return null;
}
